"use client";

import { useT } from "@/lib/i18n";
// Live roster: who is in a run with the SpireCodex mod right now.
// /api/presence/active gives identity + progress (heartbeats every ~30s,
// 90s Mongo TTL); /api/presence/{steam_id} adds each player's deck and
// relics. Contract: markdown-docs/live-presence.md.
//
// Deliberately unlisted for now (noindex, no nav link, out of the search
// palette and sitemap) but viewable by anyone with the URL. The presence
// API only ever contains players who opted into sharing.

import { useState } from "react";
import { Link } from "@/i18n/navigation";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import { imageUrl } from "@/lib/image-url";
import { GameTablesProvider } from "@/app/components/GameMessagesProvider";
import { LIVE_TABLES } from "@/lib/game-messages.common";
import {
  CardPill,
  RelicPill,
  cleanId,
  displayName,
  type CardInfo,
  type RelicInfo,
} from "../runs/[hash]/RunPills";
import {
  API,
  CharacterIcon,
  FightingChip,
  LiveCardImg,
  LiveDot,
  PartnerBadge,
  WatchOnTwitch,
  characterName,
  elapsed,
  parseDeckId,
  screenLabel,
  useCharacterNames,
  useIdMap,
  useMonsterMap,
  usePoll,
  withOrdinalKeys,
  type LivePlayer,
  type MonsterMap,
} from "./live-shared";

// The roster carries no ticker events, so the contract's 10-15s roster
// guidance applies here rather than the hot 3-5s per-player cadence.
const POLL_MS = 12_000;
const RECENT_CARDS = 5;
// Bounds the per-player detail fan-out (see the poll below).
const ROSTER_LIMIT = 24;

function PlayerCard({
  p,
  cardData,
  relicData,
  characterNames,
  monsters,
  bp,
}: {
  p: LivePlayer;
  cardData: Record<string, CardInfo>;
  relicData: Record<string, RelicInfo>;
  characterNames: Record<string, string>;
  monsters: MonsterMap;
  bp: string;
}) {
  const t = useT();
  const hpPct =
    p.hp != null && p.max_hp
      ? Math.max(0, Math.min(100, (p.hp / p.max_hp) * 100))
      : null;
  // Newest acquisitions last in the deck array; show them newest-first.
  const recent = (p.deck ?? []).slice(-RECENT_CARDS).reverse();

  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4 hover:border-[var(--border-accent)] transition-colors">
      <div className="flex items-center gap-3">
        <Link href={`/live/${p.steam_id}`} className="shrink-0">
          <CharacterIcon character={p.character} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <LiveDot />
            <Link
              href={`/live/${p.steam_id}`}
              className="font-semibold text-[var(--text-primary)] truncate hover:text-[var(--accent-gold)] transition-colors"
            >
              {p.username || t("Anonymous climber")}
            </Link>
            {p.ascension != null && p.ascension > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[var(--accent-gold)]/15 text-[var(--accent-gold)] border border-[var(--accent-gold)]/30">
                A{p.ascension}
              </span>
            )}
            {(p.player_count ?? 1) > 1 && (
              <span className="text-[10px] text-[var(--text-muted)]">
                {t("co-op ×{n}", { n: p.player_count ?? 0 })}
              </span>
            )}
            {p.is_partner && <PartnerBadge />}
          </div>
          <div className="text-xs text-[var(--text-muted)] truncate">
            {characterName(p.character, characterNames)}
            {p.screen ? ` · ${screenLabel(p.screen, t)}` : ""}
            {p.started_at
              ? ` · ${t("climbing for {time}", { time: elapsed(p.started_at, t("under a minute")) })}`
              : ""}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-[var(--text-primary)] tabular-nums">
            {p.act != null ? t("Act {n}", { n: p.act }) : ""}
            {p.total_floor != null
              ? ` · ${t("F{n}", { n: p.total_floor })}`
              : ""}
          </div>
          <div className="text-xs text-[var(--text-muted)] tabular-nums">
            {p.gold != null ? t("{n} gold", { n: p.gold }) : ""}
          </div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <FightingChip p={p} monsters={monsters} />
        {p.twitch_live && p.twitch_login && (
          <WatchOnTwitch
            login={p.twitch_login}
            viewers={p.twitch_viewers}
            className="ml-auto"
          />
        )}
        <Link
          href={`/live/${p.steam_id}`}
          className={`text-xs text-[var(--accent-gold)] hover:underline whitespace-nowrap ${
            p.twitch_live && p.twitch_login ? "" : "ml-auto"
          }`}
        >
          {t("Watch live")} →
        </Link>
      </div>

      {hpPct != null && (
        <div className="mt-3">
          <div className="flex justify-between text-[10px] text-[var(--text-muted)] mb-1 tabular-nums">
            <span>{t("HP")}</span>
            <span>
              {p.hp}/{p.max_hp}
            </span>
          </div>
          <div className="h-1.5 rounded bg-[var(--bg-primary)]">
            <div
              className="h-1.5 rounded bg-danger-fill"
              style={{ width: `${hpPct}%` }}
            />
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
            {t("Latest cards")}
          </div>
          <div className="flex gap-1.5">
            {withOrdinalKeys(recent).map(({ item: raw, key }) => {
              const { id, upgraded } = parseDeckId(raw);
              return (
                <CardPill
                  key={key}
                  cardId={id}
                  upgraded={upgraded}
                  cardData={cardData}
                  bp={bp}
                  className="block w-12 shrink-0"
                >
                  <LiveCardImg
                    id={id}
                    upgraded={upgraded}
                    alt={cardData[id]?.name || displayName(`CARD.${id}`)}
                    className="w-12 h-auto rounded-sm"
                    portrait={cardData[id]?.image_url}
                  />
                </CardPill>
              );
            })}
          </div>
        </div>
      )}

      {(p.relics ?? []).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
            {t("Relics")}
          </div>
          <div className="flex flex-wrap gap-1">
            {(p.relics ?? []).map((raw) => {
              const rid = cleanId(raw);
              const info = relicData[rid];
              const src = info?.image_url
                ? imageUrl(info.image_url)
                : imageUrl(`/static/images/relics/${rid.toLowerCase()}.png`);
              return (
                <RelicPill
                  key={raw}
                  relicId={rid}
                  relicData={relicData}
                  bp={bp}
                  className="block shrink-0"
                >
                  <img
                    src={src}
                    alt={info?.name || displayName(`RELIC.${raw}`)}
                    className="w-7 h-7 object-contain"
                    crossOrigin="anonymous"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </RelicPill>
              );
            })}
          </div>
        </div>
      )}

      {p.sts2_version && (
        <div className="mt-3 text-[10px] text-[var(--text-muted)]">
          {p.sts2_version}
        </div>
      )}
    </div>
  );
}

export default function LiveClient() {
  return (
    <GameTablesProvider tables={LIVE_TABLES}>
      <LiveRoster />
    </GameTablesProvider>
  );
}

function LiveRoster() {
  const bp = useBetaPrefix();
  const t = useT();
  const [players, setPlayers] = useState<LivePlayer[] | null>(null);
  const [stale, setStale] = useState(false);
  const cardData = useIdMap<CardInfo>("/api/cards");
  const relicData = useIdMap<RelicInfo>("/api/relics");
  const characterNames = useCharacterNames();
  const monsters = useMonsterMap(
    (players ?? []).some(
      (p) => p.screen === "combat" && (p.fighting?.length ?? 0) > 0,
    ),
  );

  usePoll(async () => {
    try {
      // Cap the roster: the page shows recent cards + relics per player, which
      // means one detail fetch each (an N+1). Capping the roster bounds that
      // fan-out; a live count beyond this is vanishingly rare and the deepest
      // runs sort first anyway.
      const r = await fetch(`${API}/api/presence/active?limit=${ROSTER_LIMIT}`);
      if (!r.ok) throw new Error(`active ${r.status}`);
      const data: { players?: LivePlayer[] } = await r.json();
      const roster = data.players ?? [];
      // The roster carries identity + progress only; deck/relics live on the
      // per-player doc, so fetch those in parallel and fall back to the roster
      // entry if one fetch fails.
      const detailed = await Promise.all(
        roster.map(async (p) => {
          try {
            const dr = await fetch(`${API}/api/presence/${p.steam_id}`);
            if (!dr.ok) return p;
            return { ...p, ...(await dr.json()) } as LivePlayer;
          } catch {
            return p;
          }
        }),
      );
      setPlayers(detailed);
      setStale(false);
    } catch {
      setStale(true);
      setPlayers((prev) => prev ?? []);
    }
  }, POLL_MS);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-1">
        <h1 className="text-3xl font-bold">
          <span className="text-[var(--accent-gold)]">{t("Live now")}</span>
        </h1>
        {players !== null && players.length > 0 && <LiveDot />}
      </div>
      <p className="text-sm text-[var(--text-muted)] mb-6">
        {t("Players in a run with the mod right now · refreshes every {n}s", {
          n: POLL_MS / 1000,
        })}
        {stale ? ` · ${t("last refresh failed, retrying")}` : ""}
      </p>

      {players === null && (
        <p className="text-sm text-[var(--text-muted)]">
          {t("Loading the roster...")}
        </p>
      )}
      {players !== null && players.length === 0 && (
        <p className="text-sm text-[var(--text-secondary)]">
          {t("Nobody is climbing right now. The roster updates on its own.")}
        </p>
      )}
      {players !== null && players.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {players.map((p) => (
            <PlayerCard
              key={p.steam_id}
              p={p}
              cardData={cardData}
              relicData={relicData}
              characterNames={characterNames}
              monsters={monsters}
              bp={bp}
            />
          ))}
        </div>
      )}
    </div>
  );
}
